import React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import SvgIcon from '@mui/material/SvgIcon'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import { ReactComponent as CloseIcon } from '../../../../../assets/images/Close.svg'

type CommentActionDialogProps = {
    open: boolean
    value: string
    isLoading: boolean
    readOnly?: boolean
    onChange: (value: string) => void
    onClose: () => void
    onSave: () => void
}

export const CommentActionDialog = ({
    open,
    value,
    isLoading,
    readOnly = false,
    onChange,
    onClose,
    onSave,
}: CommentActionDialogProps) => (
    <Dialog
        open={open}
        onClose={isLoading ? undefined : onClose}
        fullWidth
        maxWidth="sm"
        aria-labelledby="comment-action-dialog-title"
        BackdropProps={{
            sx: {
                zIndex: 1998,
            },
        }}
        sx={{
            zIndex: 1999,
        }}
        PaperProps={{
            sx: {
                py: 4,
                px: 8,
                borderRadius: '24px',
                boxShadow: '0px 15px 50px 0px rgba(0, 0, 0, 0.30)',
                position: 'relative',
                zIndex: 1999,
            },
        }}
    >
        <DialogTitle id="comment-action-dialog-title" sx={{ p: 0 }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                pb={1.5}
                borderBottom="1px solid"
                borderColor="figma.grayscale.stroke"
            >
                <Typography variant="22x600" color="figma.grayscale.textHier1">
                    {readOnly ? 'Action' : 'Edit Action'}
                </Typography>
                <IconButton
                    onClick={onClose}
                    disabled={isLoading}
                    size="small"
                    sx={{
                        p: 0.5,
                        color: 'figma.grayscale.textHier2',
                    }}
                >
                    <SvgIcon component={CloseIcon} inheritViewBox fontSize="medium" color="inherit" />
                </IconButton>
            </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
            <Stack gap={1.5} py={3}>
                <Typography variant="14x400" color="figma.grayscale.textHier2">
                    {readOnly
                        ? 'Review the action note for this comment.'
                        : 'Add or update the action note for this comment. Leave it empty to clear the action.'}
                </Typography>
                <TextField
                    autoFocus
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={8}
                    disabled={isLoading || readOnly}
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    onKeyDown={(event) => {
                        if (isLoading) {
                            return
                        }

                        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                            event.preventDefault()
                            onSave()
                        }
                    }}
                    variant="outlined"
                    placeholder="Add action"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '16px',
                            bgcolor: 'figma.grayscale.contentWrapper',
                            alignItems: 'flex-start',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'figma.grayscale.stroke',
                        },
                        '& .MuiInputBase-input': {
                            color: 'figma.grayscale.textHier1',
                            fontSize: '16px',
                            fontWeight: 500,
                            lineHeight: 1.5,
                            px: 2,
                            py: 1.75,
                        },
                    }}
                />
            </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 0 }}>
            <Stack direction="row" justifyContent="flex-end" gap={2} flex={1}>
                <Button
                    onClick={onClose}
                    disabled={isLoading}
                    // @ts-expect-error mui type
                    variant="outlined-sm-button"
                    color="primary"
                >
                    {readOnly ? 'Close' : 'Cancel'}
                </Button>
                {!readOnly && (
                    <Button
                        onClick={onSave}
                        disabled={isLoading}
                        // @ts-expect-error mui type
                        variant="contained-sm-button"
                        color="primary"
                    >
                        {isLoading ? (
                            <Stack direction="row" alignItems="center" gap={1}>
                                <CircularProgress size={16} color="inherit" />
                                <span>Saving</span>
                            </Stack>
                        ) : (
                            'Save'
                        )}
                    </Button>
                )}
            </Stack>
        </DialogActions>
    </Dialog>
)
